// The shell: header with the module steps, the File menu, undo / redo, language,
// presentation mode, and the router that mounts one module at a time (#/data, #/slope,
// #/tunnel, #/report). Data & sets loads with the page; the other modules load on demand.
// GPL-3.0-or-later.
import { t, lang, setLang, onLang } from "../i18n/index.js";
import { $, esc, isTyping, download } from "../lib/dom.js";
import * as M from "../lib/motion.js";
import { toast } from "../lib/toast.js";
import * as store from "../state/store.js";
import * as db from "../state/persist.js";
import { newProject } from "@dipfield/project";
import type { ModuleApi, ModuleId, ModuleInstance } from "../modules/types.js";
import { dataModule } from "../modules/data/index.js";
import { openProjectText, saveProjectFile, svgToPng } from "./files.js";
import { showProjects, showAbout, newProjectNow } from "./projects.js";

const ORDER: ModuleId[] = ["data", "slope", "tunnel", "report"];
const LOADERS: Record<ModuleId, () => Promise<ModuleApi>> = { data: async () => dataModule, slope: async () => (await import("../modules/slope/index.js")).slopeModule,
  tunnel: async () => (await import("../modules/tunnel/index.js")).tunnelModule, report: async () => (await import("../modules/report/index.js")).reportModule };
const title = (id: ModuleId) => ({ data: t("Data & sets"), slope: t("Slope"), tunnel: t("Tunnel"), report: t("Report") }[id]);

let current: ModuleId = "data", inst: ModuleInstance | null = null, mountSeq = 0;
const ico = (d: string) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;

function headerHTML() {
  const nav = ORDER.map((id, i) => `${i ? `<span class="arrow" aria-hidden="true">›</span>` : ""}<a href="#/${id}" data-mod="${id}"${id === current ? ' aria-current="page"' : ""}><span class="step">${i + 1}</span>${title(id)}</a>`).join("");
  const item = (id: string, icon: string, label: string, hint = "") => `<button class="btn menu-item" id="${id}">${ico(icon)}${label}${hint ? `<span class="muted small" style="margin-left:auto">${hint}</span>` : ""}</button>`;
  return `<header class="top">
  <div class="brandwrap">
    <span class="mark" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="13" stroke="currentColor" stroke-width="1.6"/>
      <g class="m-ring" stroke="currentColor" stroke-width="1.2" opacity=".55"><path d="M16 3a13 13 0 0 1 0 26"/><path d="M6.5 7.5A13 13 0 0 0 16 29" opacity=".6"/></g>
      <circle cx="20.5" cy="11" r="2.4" fill="currentColor"/><circle cx="11" cy="20" r="1.6" fill="currentColor" opacity=".55"/></svg></span>
    <h1 class="brand">DipField <span class="brand-suite">Suite</span></h1>
  </div>
  <nav class="modnav" aria-label="${t("Modules")}">${nav}</nav>
  <div class="actions">
    <button class="projname" id="projname" title="${t("Projects")}"><small>${t("Project")}</small><span id="projname-t"></span></button>
    <button class="btn ghost ico wide" id="toggle-left" aria-expanded="true" title="${t("Show or hide the data panel")}">${ico('<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/>')}<span class="lbl">${t("Data")}</span></button>
    <button class="btn ghost ico wide" id="toggle-right" aria-expanded="true" title="${t("Show or hide the tools panel")}">${ico('<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M15 4v16"/>')}<span class="lbl">${t("Tools")}</span></button>
    <span class="vsep"></span>
    <button class="btn ghost ico" id="undo" title="${t("Undo (Ctrl+Z)")}" aria-label="${t("Undo")}" disabled>${ico('<path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-4"/>')}</button>
    <button class="btn ghost ico" id="redo" title="${t("Redo (Ctrl+Y)")}" aria-label="${t("Redo")}" disabled>${ico('<path d="m15 14 5-5-5-5"/><path d="M20 9H9a5 5 0 0 0 0 10h4"/>')}</button>
    <button class="btn ghost" id="lang" style="font-weight:700;letter-spacing:.04em;padding:.3rem .55rem" title="${lang === "en" ? "สลับเป็นภาษาไทย" : "Switch to English"}">${lang === "en" ? "TH" : "EN"}</button>
    <button class="btn ghost ico" id="present" title="${t("Presentation mode (P)")}" aria-label="${t("Presentation mode")}" aria-pressed="${document.documentElement.classList.contains("present")}">${ico('<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>')}</button>
    <button class="btn ghost ico" id="fx" title="${t("Animation on / off")}" aria-label="${t("Animation on / off")}" aria-pressed="${M.fxEnabled()}" style="${M.fxEnabled() ? "" : "opacity:.45"}">${ico('<path d="M13 2 4.5 13H11l-1 9 8.5-11H12l1-9Z"/>')}</button>
    <details class="menu" id="filemenu">
      <summary class="btn">${t("File")}<svg class="chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg></summary>
      <div class="menu-pop">
        ${item("new", '<path d="M12 5v14M5 12h14"/>', t("New project"))}
        ${item("projects", '<path d="M4 6h16M4 12h16M4 18h10"/>', t("Projects…"))}
        <div class="menu-sep"></div>
        ${item("open", '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>', t("Open project file…"))}
        ${item("save", '<path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M4 20h16"/>', t("Save project file"), "Ctrl+S")}
        <div class="menu-sep"></div>
        ${item("svg", '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 15l5-4 4 3 3-2 6 4"/>', t("Export SVG"))}
        ${item("png", '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="m4 18 5-4 4 3 3-2 4 3"/>', t("Export PNG"))}
        <div class="menu-sep"></div>
        ${item("about", '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/>', t("About DipField Suite"))}
      </div>
    </details>
    <input id="open-file" type="file" accept=".json" hidden>
  </div>
</header><div id="module"></div>`;
}

function syncHeader() {
  const u = $<HTMLButtonElement>("undo"), r = $<HTMLButtonElement>("redo");
  if (u) u.disabled = !store.canUndo(); if (r) r.disabled = !store.canRedo();
  const pn = $("projname-t"); if (pn) pn.textContent = store.project().meta.name;
  const panels = inst?.panels?.();
  for (const side of ["left", "right"] as const) {
    const b = $<HTMLButtonElement>("toggle-" + side); if (!b) continue;
    b.hidden = !panels; if (panels) b.setAttribute("aria-expanded", String(panels[side]));
  }
  const exp = !!inst?.exportSVG; ["svg", "png"].forEach((id) => { const b = $<HTMLButtonElement>(id); if (b) b.disabled = !exp; });
}

async function mountModule(id: ModuleId) {
  const seq = ++mountSeq;
  const api = await LOADERS[id]();
  if (seq !== mountSeq) return;
  inst?.unmount(); inst = null;
  current = id;
  document.querySelectorAll<HTMLAnchorElement>(".modnav a").forEach((a) => (a.dataset.mod === id ? a.setAttribute("aria-current", "page") : a.removeAttribute("aria-current")));
  inst = api.mount($("module"));
  if (document.documentElement.classList.contains("present")) inst.present?.(true);
  syncHeader();
}
const routeOf = (): ModuleId => { const m = /^#\/(\w+)/.exec(location.hash); return (m && (ORDER as string[]).includes(m[1]) ? m[1] : "data") as ModuleId; };

function setPresent(on: boolean) {
  document.documentElement.classList.toggle("present", on);
  $("present")?.setAttribute("aria-pressed", String(on));
  inst?.present?.(on); syncHeader();
  toast(on ? t("Presentation mode · press P to leave") : t("Presentation mode off"));
}

function wireHeader() {
  const fileMenu = $<HTMLDetailsElement>("filemenu");
  const act: Record<string, () => void> = {
    new: newProjectNow, projects: () => void showProjects(), open: () => $("open-file").click(), save: saveProjectFile, about: showAbout,
    svg: () => { const f = inst?.exportSVG?.(); if (f) download(f.name + ".svg", f.svg, "image/svg+xml"); else toast(t("Nothing to export yet")); },
    png: () => { const f = inst?.exportSVG?.(); if (f) svgToPng(f.svg, f.name + ".png"); else toast(t("Nothing to export yet")); },
  };
  for (const [id, f] of Object.entries(act)) $(id).addEventListener("click", () => { fileMenu.open = false; f(); });
  $("projname").addEventListener("click", () => void showProjects());
  $("open-file").addEventListener("change", async (e) => {
    const inp = e.target as HTMLInputElement, f = inp.files?.[0]; if (!f) return; inp.value = "";
    openProjectText(await f.text(), f.name);
  });
  $("undo").addEventListener("click", store.undo); $("redo").addEventListener("click", store.redo);
  $("lang").addEventListener("click", () => setLang(lang === "en" ? "th" : "en"));
  $("present").addEventListener("click", () => setPresent(!document.documentElement.classList.contains("present")));
  $("fx").addEventListener("click", () => { M.setFx(!M.fxEnabled()); const b = $("fx"); b.setAttribute("aria-pressed", String(M.fxEnabled())); b.style.opacity = M.fxEnabled() ? "" : ".45"; toast(M.fxEnabled() ? t("Animation on") : t("Animation off")); });
  for (const side of ["left", "right"] as const) $("toggle-" + side).addEventListener("click", () => { inst?.togglePanel?.(side); syncHeader(); });
  document.addEventListener("click", (e) => { if (fileMenu.open && !fileMenu.contains(e.target as Node)) fileMenu.open = false; });
}

function render() {
  const app = $("app");
  app.innerHTML = headerHTML();
  wireHeader();
}

export async function startShell() {
  render();
  document.addEventListener("keydown", (e) => {
    const k = e.key.toLowerCase(), mod = e.ctrlKey || e.metaKey;
    if (mod && k === "s") { e.preventDefault(); saveProjectFile(); return; }
    if (isTyping(e.target)) return;
    if (mod && k === "z") { e.preventDefault(); e.shiftKey ? store.redo() : store.undo(); }
    else if (mod && k === "y") { e.preventDefault(); store.redo(); }
    else if (k === "p" && !mod && !e.altKey) { e.preventDefault(); setPresent(!document.documentElement.classList.contains("present")); }
  });
  store.on("history", syncHeader); store.on("saved", syncHeader); store.on("load", syncHeader);
  onLang(() => { render(); void mountModule(current); });
  window.addEventListener("hashchange", () => void mountModule(routeOf()));

  const restored = await store.boot().catch(() => false);
  if (!restored) store.load(newProject({ name: t("Untitled project") }));
  await mountModule(routeOf());
  performance.mark("dfsuite:ready");
  if (!(await db.available())) toast(t("This browser does not keep projects (private window?): save a project file before closing."));
  try { console.info(`[dipfield-suite] ${esc(store.project().meta.name)} · worker ${"Worker" in window ? "available" : "unavailable"}`); } catch { /* no console */ }
}
